// crewbit-admin/src/components/money/ScanReceiptPanel.tsx
//
// §Money.StubActionSweepCompletion Phase 3. Centered card modal for the
// scan-receipt HeaderQuickActions verb. File-upload variant only — no browser
// camera (deferred per Decision §0.7). Three internal stages: upload → extract
// → review/save. Uses existing `receipt-extract` edge function (verify_jwt=false)
// + `record_expense` SECDEF RPC.

import { useState } from 'react';
import { ScanLine, Upload, Loader2, Check } from 'lucide-react';
import { palette, spacing, transition, typography } from '../../styles/tokens';
import { MoneyInput } from '../common/MoneyInput';
import { canonicalMoney, parseMoney, isPositive } from '../../lib/money';
import { supabase } from '../../lib/supabase';
import { recordExpense } from '../../lib/expenseMutations';
import { useActiveBusinessStore } from '../../hooks/useActiveBusiness';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onResult: (ok: boolean, message: string) => void;
}

interface Extraction {
  vendor: string | null;
  amount: number | null;
  date: string | null;
  tax: number | null;
  category_suggestion: string | null;
  confidence: 'high' | 'medium' | 'low';
}

type Stage = 'upload' | 'extracting' | 'review';

const CATEGORY_OPTIONS = [
  'Materials',
  'Tools',
  'Vehicle',
  'Office',
  'Phone & Internet',
  'Insurance',
  'Professional Fees',
  'Meals',
  'Travel',
  'Other',
];

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function ScanReceiptPanel({ isOpen, onClose, onResult }: Props) {
  const businessId = useActiveBusinessStore((s) => s.activeBusinessId);

  const [stage, setStage] = useState<Stage>('upload');
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [storagePath, setStoragePath] = useState<string | null>(null);
  const [filename, setFilename] = useState<string | null>(null);
  const [extraction, setExtraction] = useState<Extraction | null>(null);
  const [extractError, setExtractError] = useState<string | null>(null);

  const [vendorName, setVendorName] = useState('');
  const [amount, setAmount] = useState(canonicalMoney(0));
  const [taxAmount, setTaxAmount] = useState(canonicalMoney(0));
  const [expenseDate, setExpenseDate] = useState(todayIso());
  const [category, setCategory] = useState<string>('Materials');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  if (!isOpen) return null;

  const reset = () => {
    setStage('upload');
    setStoragePath(null);
    setFilename(null);
    setExtraction(null);
    setExtractError(null);
    setVendorName('');
    setAmount(canonicalMoney(0));
    setTaxAmount(canonicalMoney(0));
    setExpenseDate(todayIso());
    setCategory('Materials');
    setDescription('');
    setIdempotencyKey(crypto.randomUUID());
  };

  const handleClose = () => {
    if (saving || stage === 'extracting') return;
    reset();
    onClose();
  };

  const handleFile = async (file: File) => {
    if (!businessId) {
      setExtractError('Pick a business first.');
      return;
    }
    setStage('extracting');
    setExtractError(null);

    try {
      const path = `${businessId}/scans/${Date.now()}_${file.name}`;
      const { error: uploadErr } = await supabase.storage
        .from('receipts')
        .upload(path, file, { contentType: file.type });
      if (uploadErr) {
        setExtractError(uploadErr.message);
        setStage('upload');
        return;
      }
      setStoragePath(path);
      setFilename(file.name);

      const { data, error: fnError } = await supabase.functions.invoke('receipt-extract', {
        body: { receiptUrl: path, businessId },
      });

      if (fnError) {
        setExtractError(fnError.message ?? 'OCR failed.');
        setStage('review');
        return;
      }
      if (data?.error === 'limit_reached') {
        setExtractError(data.message ?? 'Receipt scan limit reached. Try again later.');
        setStage('review');
        return;
      }
      if (data?.error) {
        setExtractError(String(data.error));
        setStage('review');
        return;
      }
      if (data?.success && data?.data) {
        const ex = data.data as Extraction;
        setExtraction(ex);
        setVendorName(ex.vendor ?? '');
        if (ex.amount != null) setAmount(canonicalMoney(ex.amount));
        if (ex.tax != null) setTaxAmount(canonicalMoney(ex.tax));
        if (ex.date) setExpenseDate(ex.date);
        if (ex.category_suggestion && CATEGORY_OPTIONS.includes(ex.category_suggestion)) {
          setCategory(ex.category_suggestion);
        }
      }
      setStage('review');
    } catch (e) {
      setExtractError(e instanceof Error ? e.message : 'OCR failed.');
      setStage('review');
    }
  };

  const handleSave = async () => {
    if (!businessId) return;
    if (!isPositive(parseMoney(amount))) return;

    setSaving(true);
    const result = await recordExpense({
      businessId,
      amount: Number(parseMoney(amount)),
      category,
      idempotencyKey,
      vendorName: vendorName.trim() || null,
      taxAmount: Number(parseMoney(taxAmount)) || null,
      expenseDate,
      description: description.trim() || null,
      receiptUrl: storagePath,
      receiptFilename: filename,
      ocrData: extraction ? (extraction as unknown as Record<string, unknown>) : null,
      ocrConfidence: extraction
        ? (extraction.confidence === 'high' ? 0.9 : extraction.confidence === 'medium' ? 0.6 : 0.3)
        : null,
      aiCategorized: !!extraction?.category_suggestion,
    });
    setSaving(false);

    if (result.success) {
      onResult(true, result.data.already_created
        ? 'Expense already recorded.'
        : `Expense recorded${vendorName ? ` from ${vendorName.trim()}` : ''}.`);
      reset();
      onClose();
    } else {
      onResult(false, result.error.message ?? 'Failed to record expense.');
    }
  };

  const valid = stage === 'review' && isPositive(parseMoney(amount));

  return (
    <>
      <div onClick={handleClose} style={{
        position: 'fixed', inset: 0, zIndex: 70,
        background: 'rgba(0,0,0,0.3)',
        backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
      }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100%', maxWidth: 480, zIndex: 71,
        background: palette.white, borderRadius: 16,
        boxShadow: '0 16px 64px rgba(0,0,0,0.14), 0 4px 16px rgba(0,0,0,0.08)',
        padding: 28,
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: 14,
          background: palette.accentWash,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: spacing.md,
        }}>
          <ScanLine size={22} color={palette.accent} strokeWidth={1.8} />
        </div>

        <div style={{ fontSize: 16, fontWeight: 700, color: palette.g800, marginBottom: 6 }}>
          Scan receipt
        </div>
        <div style={{ fontSize: 14, color: palette.g500, lineHeight: 1.5, marginBottom: spacing.md }}>
          Upload a receipt photo or PDF. We extract vendor, amount, date, and tax — confirm and save.
        </div>

        {stage === 'upload' && (
          <UploadStage onFile={handleFile} error={extractError} />
        )}

        {stage === 'extracting' && (
          <div style={{
            padding: spacing.lg, textAlign: 'center',
            background: palette.g100, borderRadius: 12,
          }}>
            <Loader2 size={24} color={palette.accent} style={{ animation: 'spin 1s linear infinite' }} />
            <div style={{ marginTop: spacing.sm, color: palette.g700, fontSize: typography.size.caption }}>
              Reading receipt…
            </div>
          </div>
        )}

        {stage === 'review' && (
          <>
            {extractError && (
              <div style={{
                padding: spacing.sm, background: palette.amberWash,
                border: `1px solid ${palette.amber}`, borderRadius: 8,
                fontSize: typography.size.caption, color: palette.g800, marginBottom: spacing.sm,
              }}>
                {extractError} You can still fill the fields manually.
              </div>
            )}
            {extraction && (
              <div style={{
                padding: spacing.sm, background: palette.greenWash,
                border: `1px solid ${palette.green}`, borderRadius: 8,
                display: 'flex', alignItems: 'center', gap: spacing.xs,
                fontSize: typography.size.caption, color: palette.g800, marginBottom: spacing.sm,
              }}>
                <Check size={14} color={palette.green} />
                Extracted with {extraction.confidence} confidence — review before saving.
              </div>
            )}

            <div style={{ display: 'grid', gap: spacing.sm }}>
              <Field label="Vendor">
                <input
                  type="text"
                  value={vendorName}
                  onChange={(e) => setVendorName(e.target.value.slice(0, 200))}
                  placeholder="Vendor name"
                  style={inputStyle}
                />
              </Field>

              <Field label="Amount" required>
                <MoneyInput value={amount} onChange={setAmount} ariaLabel="Expense amount" />
              </Field>

              <Field label="Tax">
                <MoneyInput value={taxAmount} onChange={setTaxAmount} ariaLabel="Tax amount" />
              </Field>

              <Field label="Date">
                <input
                  type="date"
                  value={expenseDate}
                  onChange={(e) => setExpenseDate(e.target.value)}
                  style={inputStyle}
                />
              </Field>

              <Field label="Category">
                <select value={category} onChange={(e) => setCategory(e.target.value)} style={inputStyle}>
                  {CATEGORY_OPTIONS.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </Field>

              <Field label="Description">
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value.slice(0, 500))}
                  placeholder="Optional"
                  style={inputStyle}
                />
              </Field>
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: spacing.md }}>
          <button onClick={handleClose} disabled={saving || stage === 'extracting'} style={{
            padding: '9px 22px', borderRadius: 20,
            border: `1px solid ${palette.g200}`, background: palette.white,
            fontSize: 13.5, fontWeight: 500, color: palette.g600,
            cursor: (saving || stage === 'extracting') ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            transition: `background ${transition.fast}`,
          }}>Cancel</button>
          {stage === 'review' && (
            <button onClick={handleSave} disabled={saving || !valid} style={{
              padding: '9px 22px', borderRadius: 20, border: 'none',
              background: (saving || !valid) ? palette.g300 : palette.accent,
              color: palette.white, fontSize: 13.5, fontWeight: 600,
              cursor: (saving || !valid) ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            }}>{saving ? 'Saving…' : 'Save expense'}</button>
          )}
        </div>
      </div>
    </>
  );
}

function UploadStage({ onFile, error }: { onFile: (file: File) => void; error: string | null }) {
  const [dragOver, setDragOver] = useState(false);

  return (
    <div>
      <label
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const file = e.dataTransfer.files[0];
          if (file) onFile(file);
        }}
        style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          padding: spacing.xl, borderRadius: 12,
          border: `2px dashed ${dragOver ? palette.accent : palette.g200}`,
          background: dragOver ? palette.accentWash : palette.g100,
          cursor: 'pointer', minHeight: 160,
          transition: `background 120ms, border-color 120ms`,
        }}
      >
        <Upload size={28} color={palette.g500} />
        <div style={{ marginTop: spacing.sm, color: palette.g700, fontSize: typography.size.caption, fontWeight: 600 }}>
          Drop a receipt here or click to upload
        </div>
        <div style={{ marginTop: 4, color: palette.g500, fontSize: 11.5 }}>
          JPG, PNG, or PDF
        </div>
        <input
          type="file"
          accept="image/*,application/pdf"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onFile(file);
          }}
          style={{ display: 'none' }}
        />
      </label>

      {error && (
        <div style={{
          marginTop: spacing.sm,
          padding: spacing.sm,
          background: palette.amberWash,
          border: `1px solid ${palette.amber}`,
          borderRadius: 8,
          fontSize: typography.size.caption,
          color: palette.g800,
        }}>
          {error}
        </div>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: spacing.sm, borderRadius: 8,
  border: `1px solid ${palette.g200}`, fontFamily: 'inherit',
  fontSize: typography.size.caption, color: palette.g800,
  boxSizing: 'border-box', background: palette.white,
};

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: typography.size.caption, color: palette.g700, marginBottom: spacing.xs }}>
        {label}{required && <span style={{ color: palette.red, marginLeft: 2 }}>*</span>}
      </div>
      {children}
    </label>
  );
}
